import { snackbar } from "@bcmi-labs/art-ui/components"
import type { ComponentProps } from "react"

import { isPWAOnChromebook } from "@/lib/utils/chromebook"

/**
 * Open an external link in a new tab. Validates PWA behavior on Chromebooks to ensure links open in the user's default browser instead of the PWA.
 * @param url The URL to open.
 * @param target The target attribute for the anchor element.
 */
export function openExternalLink(url: string | URL, target?: ComponentProps<"a">["target"]) {
  if (isPWAOnChromebook() && !url.toString().startsWith(window.location.origin)) {
    snackbar({
      message: "Reach the link using your browser.",
      actions: [
        {
          text: "Copy",
          onClick: async () => {
            try {
              await navigator.clipboard.writeText(url.toString())
              snackbar({ message: "Link copied to clipboard!", variant: "success" })
            } catch {
              snackbar({ message: "Link didn't copy to clipboard!", variant: "error" })
            }
          }
        }
      ]
    })
    return
  }

  // eslint-disable-next-line cloud-website/no-location-href-assign
  window.open(
    url.toString(),
    target || (url.toString().startsWith(window.location.origin) ? "_self" : "_blank"),
    "noopener,noreferrer"
  )
}

/**
 * Downloads a file from a blob
 *
 * @param blob
 * @param filename
 */
export function downloadFile(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const link = document.createElement("a")
  link.setAttribute("href", url)
  link.setAttribute("download", filename)
  link.style.visibility = "hidden"
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

/**
 * Converts a Uint8Array to a base64 string.
 */
export function uint8ArrayToBase64(data: Uint8Array) {
  // Avoid stack overflow for large arrays by processing in chunks
  let binString = ""
  const chunkSize = 0x8000 // 32kB per chunk
  for (let i = 0; i < data.length; i += chunkSize) {
    const chunk = data.subarray(i, i + chunkSize)
    binString += String.fromCharCode.apply(null, Array.from(chunk))
  }
  return btoa(binString)
}
